import { useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense } from 'react';
import { setDocumentMeta } from '@/lib/seo';
import { IS_PRERENDER } from '@/lib/hydration';
import { Link } from 'react-router-dom';
import {
  IconCircleCheck, IconAlertTriangle, IconCircleX, IconActivity, IconClock, IconRefresh,
  IconArrowUpRight, IconCopy, IconCheck, IconDatabase, IconServer, IconBolt, IconShieldCheck,
  IconWorld, IconCloudDataConnection,
} from '@tabler/icons-react';
import { MarketingShell } from '@/components/marketing/warm/marketing-shell';
import { PageHero } from '@/components/marketing/warm/page-hero';
import { Chip, IconTile, Muted, useReveal } from '@/components/marketing/warm/primitives';

const StatusLatencyChart = lazy(() => import('./status-latency-chart'));

const STATUS_API = 'https://kora-monitor.unlink-at.workers.dev/';

type CheckEntry = { t: string; s: 'up' | 'down' | 'degraded'; r: number; c?: Record<string, string> };
type Incident = {
  id: string;
  startedAt: string;
  resolvedAt: string | null;
  duration: number | null;
};

type ComponentStatus = {
  name: string;
  key: string;
  status: 'operational' | 'degraded' | 'down' | 'unknown' | 'not_configured';
};

type StatusResponse = {
  success: boolean;
  current: { status: string; responseTime: number; checkedAt: string } | null;
  overallStatus: 'operational' | 'partial_outage' | 'major_outage' | 'unknown';
  components: ComponentStatus[];
  summary: {
    uptimePercentage7d: number;
    avgResponseTime24m: number;
    totalChecks7d: number;
    incidents7d: number;
  } | null;
  history: CheckEntry[];
  incidents: Incident[];
  lastUpdated: string | null;
  checkInterval: string;
  message?: string;
};


type Tone = Parameters<typeof Chip>[0]['tone'];

const statusConfig = {
  operational: { label: 'All Systems Operational', icon: IconCircleCheck, color: '#2F8F4E', glow: 'rgba(47,143,78,0.18)', faviconColor: '#22c55e' },
  partial_outage: { label: 'Partial Service Degradation', icon: IconAlertTriangle, color: '#B7791F', glow: 'rgba(214,158,46,0.22)', faviconColor: '#eab308' },
  major_outage: { label: 'Major Service Outage', icon: IconCircleX, color: '#C2483D', glow: 'rgba(194,72,61,0.2)', faviconColor: '#ef4444' },
  unknown: { label: 'Checking System Status', icon: IconRefresh, color: '#8A8478', glow: 'rgba(138,132,120,0.15)', faviconColor: '#a1a1aa' },
};

const componentIcons: Record<string, typeof IconBolt> = {
  api: IconBolt,
  database: IconDatabase,
  storage: IconCloudDataConnection,
};

const componentStatusConfig: Record<string, { label: string; tone: Tone }> = {
  operational: { label: 'Operational', tone: 'green' },
  degraded: { label: 'Degraded', tone: 'amber' },
  down: { label: 'Down', tone: 'red' },
  not_configured: { label: 'Not Configured', tone: 'neutral' },
  unknown: { label: 'Waiting for data', tone: 'neutral' },
};

const BAR = { up: '#4F8A5B', degraded: '#D9A441', down: '#C2483D', none: 'var(--warm-border)' };
const CARD: React.CSSProperties = { background: 'var(--warm-card)', border: '1px solid var(--warm-border)' };

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

function formatAbsoluteTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

// Generate a dynamic SVG favicon based on status color
function setFavicon(color: string) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><circle cx="16" cy="16" r="14" fill="${color}"/><circle cx="16" cy="16" r="6" fill="white" fill-opacity="0.9"/></svg>`;
  const encoded = encodeURIComponent(svg).replace(/'/g, '%27').replace(/"/g, '%22');
  const dataUri = `data:image/svg+xml,${encoded}`;
  let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.type = 'image/svg+xml';
  link.href = dataUri;
}


function Panel({ title, sub, children, className = '' }: { title: string; sub?: string; children: React.ReactNode; className?: string }) {
  const ref = useReveal<HTMLDivElement>();
  return (
    <div ref={ref} className={`home-reveal rounded-2xl p-6 ${className}`} style={CARD}>
      <h2 className="text-sm font-semibold" style={{ color: 'var(--warm-text)' }}>{title}</h2>
      {sub && <p className="text-xs mt-0.5" style={{ color: 'var(--warm-muted)' }}>{sub}</p>}
      <div className="mt-4">{children}</div>
    </div>
  );
}

function MetricCard({ label, value, sublabel, icon }: { label: string; value: string | number; sublabel?: string; icon: typeof IconBolt }) {
  return (
    <div className="rounded-2xl p-5" style={CARD}>
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--warm-muted)' }}>{label}</span>
        <IconTile icon={icon} />
      </div>
      <p className="font-display font-light text-3xl leading-none" style={{ color: 'var(--warm-text)' }}>{value}</p>
      {sublabel && <p className="text-xs mt-2" style={{ color: 'var(--warm-muted)' }}>{sublabel}</p>}
    </div>
  );
}

function ComponentStatusRow({ component, first }: { component: ComponentStatus; first: boolean }) {
  const Icon = componentIcons[component.key] || IconServer;
  const cfg = componentStatusConfig[component.status] || componentStatusConfig.unknown;
  return (
    <div className="flex items-center justify-between gap-3 py-3" style={first ? undefined : { borderTop: '1px solid var(--warm-border)' }}>
      <div className="flex items-center gap-3">
        <IconTile icon={Icon} />
        <span className="text-sm font-medium" style={{ color: 'var(--warm-text)' }}>{component.name}</span>
      </div>
      <Chip tone={cfg.tone}>{cfg.label}</Chip>
    </div>
  );
}

function UptimeOverview({ history }: { history: CheckEntry[] }) {
  const days = useMemo(() => {
    const dayMap = new Map<string, { up: number; total: number; degraded: number; date: Date }>();
    const now = Date.now();

    for (let i = 89; i >= 0; i--) {
      const date = new Date(now - i * 24 * 60 * 60 * 1000);
      const key = date.toISOString().slice(0, 10);
      dayMap.set(key, { up: 0, total: 0, degraded: 0, date });
    }

    for (const check of history) {
      const key = check.t.slice(0, 10);
      const day = dayMap.get(key);
      if (day) {
        day.total++;
        if (check.s === 'up') day.up++;
        if (check.s === 'degraded') day.degraded++;
      }
    }

    return Array.from(dayMap.entries()).map(([date, stats]) => ({
      key: date,
      uptime: stats.total > 0 ? (stats.up / stats.total) * 100 : 100,
      hasData: stats.total > 0,
      isDegraded: stats.degraded > 0 && stats.up > 0,
      isDown: stats.total > 0 && stats.up === 0,
      label: stats.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    }));
  }, [history]);

  return (
    <Panel title="90-Day Uptime Overview">
      <div className="overflow-x-auto pb-2 -mx-1 px-1">
        <div className="flex gap-[3px] min-w-[480px]">
          {days.map((day) => (
            <div
              key={day.key}
              className="group relative flex-1 min-w-[4px] h-9 rounded-[3px] transition-opacity hover:opacity-75"
              role="img"
              style={{ backgroundColor: !day.hasData ? BAR.none : day.isDown ? BAR.down : day.isDegraded ? BAR.degraded : BAR.up }}
              aria-label={`${day.label}: ${day.hasData ? day.uptime.toFixed(2) + '% uptime' : 'no data'}`}
            >
              <div className="invisible group-hover:visible absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-20 whitespace-nowrap rounded-lg px-2 py-1 text-xs shadow-md pointer-events-none"
                style={{ background: 'var(--warm-inv)', color: 'var(--warm-inv-text)' }}>
                <span className="font-medium">{day.label}</span>
                {day.hasData ? ` · ${day.uptime.toFixed(2)}%` : ' · No data'}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-4 mt-3 text-xs" style={{ color: 'var(--warm-muted)' }}>
        {[['Operational', BAR.up], ['Degraded', BAR.degraded], ['Down', BAR.down]].map(([l, c]) => (
          <span key={l} className="flex items-center gap-1.5"><span className="size-2.5 rounded-sm" style={{ background: c }} />{l}</span>
        ))}
        <span className="ml-auto">90 days ago</span>
        <span>Today</span>
      </div>
    </Panel>
  );
}

function LatencyChart({ history }: { history: CheckEntry[] }) {
  const chartData = useMemo(() => {
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return history
      .filter((h) => new Date(h.t).getTime() > cutoff && h.s !== 'down')
      .map((h) => ({
        time: new Date(h.t).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }),
        latency: h.r,
      }));
  }, [history]);

  return (
    <Panel title="Check Latency" sub="Last 7 days · measured from real API requests">
      {chartData.length === 0 ? (
        <div className="flex items-center justify-center h-[200px] text-sm" style={{ color: 'var(--warm-muted)' }}>Collecting data...</div>
      ) : (
        <Suspense fallback={<div className="h-[200px]" />}>
          <StatusLatencyChart data={chartData} />
        </Suspense>
      )}
    </Panel>
  );
}

function IncidentList({ incidents }: { incidents: Incident[] }) {
  return (
    <Panel title="Incident History">
      {incidents.length === 0 ? (
        <div className="py-6 text-center">
          <IconCircleCheck size={28} stroke={1.5} className="mx-auto" style={{ color: BAR.up }} />
          <p className="text-sm mt-2" style={{ color: 'var(--warm-muted)' }}>No incidents in the last 7 days.</p>
        </div>
      ) : (
        <ol className="space-y-3">
          {incidents.map((inc, i) => (
            <li key={inc.id} className="flex items-start gap-3 pt-3" style={i ? { borderTop: '1px solid var(--warm-border)' } : { paddingTop: 0 }}>
              {inc.resolvedAt
                ? <IconCircleCheck size={18} stroke={1.75} className="shrink-0 mt-0.5" style={{ color: BAR.up }} />
                : <IconCircleX size={18} stroke={1.75} className="shrink-0 mt-0.5 animate-pulse" style={{ color: BAR.down }} />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium" style={{ color: 'var(--warm-text)' }}>{inc.resolvedAt ? 'Resolved Incident' : 'Ongoing Incident'}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--warm-muted)' }}>
                  <span title={formatAbsoluteTime(inc.startedAt)}>Started {formatRelativeTime(inc.startedAt)}</span>
                  {inc.resolvedAt && inc.duration ? (
                    <>
                      {' · '}
                      <span title={formatAbsoluteTime(inc.resolvedAt)}>Resolved {formatRelativeTime(inc.resolvedAt)}</span>
                      {' · '}
                      <span className="font-medium">Duration {formatDuration(inc.duration)}</span>
                    </>
                  ) : ' · Still ongoing'}
                </p>
              </div>
            </li>
          ))}
        </ol>
      )}
    </Panel>
  );
}

function InfrastructureInfo() {
  const rows: [typeof IconBolt, string, string][] = [
    [IconWorld, 'Data Region', 'European Union (EEUR)'],
    [IconShieldCheck, 'Data Residency', 'EU Jurisdiction · GDPR'],
    [IconDatabase, 'Read Replication', 'Auto (within EU)'],
    [IconClock, 'Monitoring Cadence', 'Every 5 minutes'],
  ];
  return (
    <Panel title="Infrastructure">
      {rows.map(([Icon, k, v], i) => (
        <div key={k} className="flex items-center justify-between gap-3 py-2.5" style={i ? { borderTop: '1px solid var(--warm-border)' } : undefined}>
          <div className="flex items-center gap-3">
            <IconTile icon={Icon} />
            <span className="text-sm font-medium" style={{ color: 'var(--warm-text)' }}>{k}</span>
          </div>
          <span className="text-xs font-medium text-right" style={{ color: 'var(--warm-muted)' }}>{v}</span>
        </div>
      ))}
    </Panel>
  );
}

export function StatusPage() {
  const [data, setData] = useState<StatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [lastFetch, setLastFetch] = useState<Date | null>(null);
  const [copied, setCopied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const originalTitle = useRef(document.title);
  const originalFavicon = useRef<string | null>(null);

  const fetchStatus = useCallback(async (manual = false) => {
    if (manual) setRefreshing(true);
    try {
      const res = await fetch(STATUS_API);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json: StatusResponse = await res.json();
      setData(json);
      setError(false);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLastFetch(new Date());
    }
  }, []);

  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
    if (link) originalFavicon.current = link.href;

    // Live data must not be baked into prerendered HTML: the visitor's first
    // render shows the loading state, so the snapshot has to match it.
    if (IS_PRERENDER) return;
    fetchStatus();
    const interval = setInterval(() => fetchStatus(), 60_000);
    return () => {
      clearInterval(interval);
    };
  }, [fetchStatus]);

  useEffect(() => {
    // Also resets robots to index,follow (the HTML shell defaults to noindex).
    setDocumentMeta('Kora Status — System Health & Uptime', 'Real-time system health, uptime, and incident monitoring for Kora. EU-hosted infrastructure with 5-minute health checks.');

    // Canonical
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = 'https://usekora.app/status';

    // JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Kora Status — System Health & Uptime',
      description: 'Real-time system health, uptime, and incident monitoring for Kora.',
      url: 'https://usekora.app/status',
    });
    script.setAttribute('data-seo-jsonld', 'true');
    document.head.appendChild(script);

    return () => {
      document.title = originalTitle.current;
      script.remove();
    };
  }, []);

  const overallStatus = data?.overallStatus || 'unknown';
  useEffect(() => {
    const config = statusConfig[overallStatus];
    setFavicon(config.faviconColor);
    return () => {
      const link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
      if (link && originalFavicon.current) {
        link.href = originalFavicon.current;
      }
    };
  }, [overallStatus]);

  const config = statusConfig[overallStatus];
  const StatusIcon = config.icon;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = window.location.href;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };


  const btn = 'inline-flex items-center gap-1.5 rounded-[10px] px-3.5 py-2 text-sm font-medium transition-colors disabled:opacity-50';
  const btnStyle: React.CSSProperties = { background: 'var(--warm-card)', border: '1px solid var(--warm-border)', color: 'var(--warm-text)' };

  return (
    <MarketingShell>
      <PageHero
        eyebrow="System status"
        title={<>Kora <Muted><span className="italic">Status</span></Muted></>}
        lead="Live system health for the Kora support platform. Infrastructure is hosted in the European Union with health checks running every 5 minutes."
        actions={<>
          <button onClick={() => fetchStatus(true)} disabled={refreshing} className={btn} style={btnStyle} aria-label="Refresh status">
            <IconRefresh size={15} stroke={1.75} className={refreshing ? 'animate-spin' : ''} /> Refresh
          </button>
          <button onClick={handleCopyLink} className={btn} style={btnStyle} aria-label="Share: copy status page link">
            {copied ? <><IconCheck size={15} stroke={2} style={{ color: 'var(--chip-green)' }} /> Copied!</> : <><IconCopy size={15} stroke={1.75} /> Share</>}
          </button>
        </>}
        aside={
          <div className="rounded-3xl p-7 md:p-9 relative overflow-hidden" style={{ ...CARD, background: `radial-gradient(120% 120% at 0% 0%, ${config.glow} 0%, transparent 60%), var(--warm-card)` }} aria-live="polite">
            <div className="flex items-center gap-3">
              <span className="relative flex size-3">
                <span className="absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping" style={{ background: config.color }} />
                <span className="relative inline-flex size-3 rounded-full" style={{ background: config.color }} />
              </span>
              <StatusIcon size={22} stroke={1.75} style={{ color: config.color }} />
            </div>
            <p className="mt-5 font-display font-light text-3xl md:text-4xl leading-tight" style={{ color: 'var(--warm-text)' }}>{config.label}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {data?.current && <Chip tone="neutral" wrap><IconBolt size={12} stroke={2} /> {data.current.responseTime}ms current latency</Chip>}
              {lastFetch && (
                <Chip tone="neutral" wrap>
                  <IconClock size={12} stroke={2} />
                  {data?.lastUpdated ? `Last checked ${formatRelativeTime(data.lastUpdated)} · Auto-refreshes every 60s` : 'Waiting for first check...'}
                </Chip>
              )}
            </div>
          </div>
        }
      />

      <section className="pb-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          {loading ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => <div key={i} className="h-32 rounded-2xl animate-pulse" style={{ background: 'var(--warm-card)' }} />)}
              </div>
              <div className="h-64 rounded-2xl animate-pulse" style={{ background: 'var(--warm-card)' }} />
            </div>
          ) : error ? (
            <div className="rounded-2xl p-10 text-center" style={{ background: 'var(--chip-red-bg)', border: '1px solid var(--warm-border)' }}>
              <IconCircleX size={30} stroke={1.5} className="mx-auto mb-3" style={{ color: BAR.down }} />
              <p className="text-sm font-medium" style={{ color: 'var(--chip-red)' }}>Failed to load status data.</p>
              <button onClick={() => fetchStatus(true)} className="mt-3 inline-flex items-center gap-1.5 text-sm hover:underline" style={{ color: 'var(--chip-red)' }}>
                <IconRefresh size={14} stroke={1.75} /> Retry
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="reveal-stagger is-visible grid grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard
                  label="Status"
                  value={data?.current?.status === 'up' ? 'Up' : data?.current?.status === 'down' ? 'Down' : '—'}
                  sublabel={data?.current ? formatRelativeTime(data.current.checkedAt) : undefined}
                  icon={IconCircleCheck}
                />
                <MetricCard
                  label="Uptime (7d)"
                  value={data?.summary ? `${data.summary.uptimePercentage7d}%` : '—'}
                  sublabel={data?.summary ? `${data.summary.totalChecks7d} checks` : undefined}
                  icon={IconActivity}
                />
                <MetricCard
                  label="Avg Latency (24m)"
                  value={data?.summary ? `${data.summary.avgResponseTime24m}ms` : '—'}
                  sublabel="From real API requests"
                  icon={IconClock}
                />
                <MetricCard
                  label="Incidents (7d)"
                  value={data?.summary?.incidents7d ?? 0}
                  sublabel={data?.summary?.incidents7d === 0 ? 'No incidents' : undefined}
                  icon={IconAlertTriangle}
                />
              </div>

              {data?.components && data.components.length > 0 && (
                <Panel title="Service Components">
                  {data.components.map((comp, i) => <ComponentStatusRow key={comp.key} component={comp} first={i === 0} />)}
                </Panel>
              )}

              <LatencyChart history={data?.history || []} />
              <UptimeOverview history={data?.history || []} />

              <div className="grid lg:grid-cols-2 gap-4 items-start">
                <InfrastructureInfo />
                <IncidentList incidents={data?.incidents || []} />
              </div>
            </div>
          )}

          <div id="footer-sentinel" className="mt-10 pt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-3" style={{ borderTop: '1px solid var(--warm-border)' }}>
            <p className="text-xs" style={{ color: 'var(--warm-muted)' }}>Health checks every 5 minutes · EU-hosted · Powered by Cloudflare Workers</p>
            <Link to="/docs" className="inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: 'var(--warm-text)' }}>
              API Docs <IconArrowUpRight size={16} stroke={1.75} />
            </Link>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
